import React from 'react';
import iconPng from '/icon.png';

type UpdateStatus =
  | { status: 'available'; version: string; startup: boolean }
  | { status: 'downloading'; percent: number; bytesPerSecond: number; transferred: number; total: number; startup: boolean }
  | { status: 'ready'; version: string; startup: boolean }
  | { status: 'error'; message: string };

interface UpdateScreenProps {
  update: UpdateStatus;
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const progressGradient = 'linear-gradient(90deg, #fb923c 0%, #f97316 100%)';

const getProgressPercent = (update: Extract<UpdateStatus, { status: 'downloading' }>) => {
  const direct = Number(update.percent);
  const fromBytes = update.total > 0 ? (update.transferred / update.total) * 100 : 0;
  const raw = Number.isFinite(direct) && direct > 0 ? direct : fromBytes;
  const clamped = Math.max(0, Math.min(100, raw));
  return clamped > 0 && clamped < 1 ? 1 : clamped;
};

const UpdateScreen: React.FC<UpdateScreenProps> = ({ update }) => {
  const progressPercent = update.status === 'downloading' ? getProgressPercent(update) : 0;

  return (
    <div className="fixed inset-0 z-[99999] bg-bg-primary flex flex-col items-center justify-center gap-6">
      {/* Logo */}
      <img src={iconPng} alt="Erozion" className="w-20 h-20 rounded-2xl shadow-2xl" />

      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-xl font-bold text-text-primary">Erozion</h1>

        {update.status === 'available' && (
          <p className="text-sm text-text-secondary">
            Mise à jour {update.version} disponible — préparation du téléchargement…
          </p>
        )}

        {update.status === 'downloading' && (
          <>
            <p className="text-sm text-text-secondary">
              Mise à jour en cours…
            </p>
            <p className="text-xs text-text-muted">
              {formatBytes(update.transferred)} / {formatBytes(update.total)}
              {update.bytesPerSecond > 0 && ` · ${formatBytes(update.bytesPerSecond)}/s`}
            </p>
          </>
        )}

        {update.status === 'ready' && (
          <p className="text-sm text-text-secondary">
            Installation en cours — l'application va redémarrer…
          </p>
        )}

        {update.status === 'error' && (
          <p className="text-sm text-error">
            Erreur : {update.message}
          </p>
        )}
      </div>

      {/* Progress bar */}
      {update.status === 'downloading' && (
        <div className="w-64 h-2 rounded-full bg-black/30 overflow-hidden ring-1 ring-white/5">
          <div
            className="h-full rounded-full transition-[width] duration-300 ease-out shadow-[0_0_12px_rgba(251,146,60,0.3)]"
            style={{ width: `${progressPercent}%`, background: progressGradient }}
          />
        </div>
      )}

      {update.status === 'ready' && (
        <div className="w-64 h-2 rounded-full overflow-hidden shadow-[0_0_12px_rgba(251,146,60,0.3)]" style={{ background: progressGradient }} />
      )}

      {/* Percent */}
      {update.status === 'downloading' && (
        <span className="text-2xl font-bold tabular-nums text-orange-400">
          {Math.round(progressPercent)}%
        </span>
      )}

      {update.status === 'error' && (
        <button
          onClick={() => window.api?.checkForUpdates?.()}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-highlight hover:bg-highlight-hover text-text-primary transition-colors"
        >
          Réessayer
        </button>
      )}
    </div>
  );
};

export default UpdateScreen;
